import { CloseDialogFn, useDialog } from '@wui/common-library'
import { isTextMessage, IMFile } from '@wui/im'
import { useRoute } from 'vue-router'

import { useCallSessionRoomStore } from '@/entities/call-session'
import { chatModel } from '@/entities/messages'
import { useWebRTC } from '@/entities/webrtc'

import { getFileTypeByName } from '@/shared/utils/files'
import {
  getPServedFromString,
  toPServedUser,
} from '@/shared/utils/uri'

import ImageDialog from './image-dialog.vue'

export const useNavigateItems = () => {
  const route = useRoute()
  const { showDialog } = useDialog()
  const { findRoomMessages } = chatModel.useChatMessageStore()

  const getRoomIdFromChatRoute = (): string | undefined => {
    const idParam = route.params.id
    if (typeof idParam === 'string') {
      return atob(idParam)
    } else if (Array.isArray(idParam) && idParam[0]) {
      return atob(idParam[0])
    }
    return
  }

  const getRoomIdFromSessionRoute = (): string | undefined => {
    const idParam = route.params.sessionId
    const { getRTCSessionFacadeById } = useWebRTC()
    if (typeof idParam === 'string') {
      const rtcSessionFacade = getRTCSessionFacadeById(idParam)
      if (!rtcSessionFacade) {
        return
      }
      return useCallSessionRoomStore().getRoom(rtcSessionFacade.sessionId)?.id
    }
    return
  }

  const nextPicture = async (src: string, closeModal: CloseDialogFn<unknown>) => {
    const id = getRoomIdFromChatRoute() ?? getRoomIdFromSessionRoute()
    if (!id) return
    const pServedGroup = toPServedUser(id, true)
    const pServedPrivate = getPServedFromString(id)
    let messages = findRoomMessages(pServedGroup).filter(isTextMessage)
    if (!messages.length) {
      messages = findRoomMessages(pServedPrivate).filter(isTextMessage)
    }
    const items = messages.reduce((acc, message) => {
      const files = message.item.files
      if (files) {
        files.forEach((file: IMFile) => {
          if (getFileTypeByName(file.filename) === 'imageFile') {
            acc.push(file)
          }
        })
      }
      return acc
    }, [] as IMFile[])
    const currentItemIndex = items.findIndex((item) => src.includes(item.filecookie))
    const targetItem = items.at(currentItemIndex + 1)
    if (targetItem) {
      closeModal(undefined)
      await showDialog(ImageDialog, {
        src: `/files/${targetItem.filecookie}`,
        name: targetItem.filename,
        hasOverlay: true,
      })
    }
  }

  const prevPicture = async (src: string, closeModal: CloseDialogFn<unknown>) => {
    const id = getRoomIdFromChatRoute() ?? getRoomIdFromSessionRoute()
    if (!id) return
    const pServedGroup = toPServedUser(id, true)
    const pServedPrivate = getPServedFromString(id)
    let messages = findRoomMessages(pServedGroup).filter(isTextMessage)
    if (!messages.length) {
      messages = findRoomMessages(pServedPrivate).filter(isTextMessage)
    }
    const items = messages.reduce((acc, message) => {
      const files = message.item.files
      if (files) {
        files.forEach((file: IMFile) => {
          if (getFileTypeByName(file.filename) === 'imageFile') {
            acc.push(file)
          }
        })
      }
      return acc
    }, [] as IMFile[])
    const currentItemIndex = items.findIndex((item) => src.includes(item.filecookie))
    const targetItem = items[currentItemIndex - 1]
    if (targetItem) {
      closeModal(undefined)
      await showDialog(ImageDialog, {
        src: `/files/${targetItem.filecookie}`,
        name: targetItem.filename,
        hasOverlay: true,
      })
    }
  }

  return {
    nextPicture,
    prevPicture,
  }
}
